import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cta } from '../../content';
import { Arrow, cx } from '../../ui';

// Renders a fixed-size design stage (w x h) scaled down to fit its container, so dense UI mockups
// keep their exact composition at every width.
export function Stage({ w, h, max = 1, className, children, label }: { w: number; h: number; max?: number; className?: string; children: React.ReactNode; label?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [s, setS] = useState(1);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const fit = () => setS(Math.min(max, el.clientWidth / w));
    fit();
    if (!('ResizeObserver' in window)) return;
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [w, max]);
  return (
    <div ref={ref} className={cx('relative w-full', className)} style={{ height: h * s }} role={label ? 'img' : undefined} aria-label={label}>
      <div style={{ position: 'absolute', left: '50%', top: 0, width: w, height: h, transform: `translateX(-50%) scale(${s})`, transformOrigin: 'top center' }}>{children}</div>
    </div>
  );
}

// True while the element is on screen (pauses loops off-screen).
export function useVisible<T extends HTMLElement>(threshold = 0.2) {
  const ref = useRef<T>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!('IntersectionObserver' in window)) {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { threshold });
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, visible] as const;
}

export const reduced = () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// Work email -> /book-a-demo?email=... (the booking page prefills it).
export function EmailCapture({ source, className, dark = false }: { source: string; className?: string; dark?: boolean }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const id = React.useId();
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    const v = email.trim();
    if (v) params.set('email', v);
    params.set('src', source);
    navigate(`${cta.primaryHref}?${params.toString()}`);
  };
  return (
    <form onSubmit={submit} className={cx('vb-capture', className)} aria-label="Book 30 minutes">
      <label htmlFor={id} className="sr-only">
        Work email
      </label>
      <input id={id} type="email" inputMode="email" autoComplete="email" placeholder="Work email" value={email} onChange={(e) => setEmail(e.target.value)} />
      <button type="submit" className={cx('btn', dark ? 'btn-go' : 'btn-primary')}>
        {cta.primary}
        <Arrow />
      </button>
    </form>
  );
}

export const MicroTrust = ({ dark = false }: { dark?: boolean }) => (
  <p className={cx('mt-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13.5px]', dark ? 'text-white/65' : 'text-bm-muted')}>
    <span className="whitespace-nowrap">One product, 30 minutes</span>
    <Dot dark={dark} />
    <span className="whitespace-nowrap">Not a sales script</span>
    <Dot dark={dark} />
    <span className="whitespace-nowrap">GDPR ✓</span>
  </p>
);

const Dot = ({ dark }: { dark?: boolean }) => <span aria-hidden="true" className={cx('inline-block h-1 w-1 rounded-full', dark ? 'bg-white/40' : 'bg-[#B9B7D3]')} />;

export const Check = ({ size = 14, className }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true" className={className}>
    <path d="M3.2 8.4 6.5 11.5 12.8 4.8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Two-tone headline (Ramp pattern): indigo statement, muted continuation.
export function H2({ lead, rest, className, as: Tag = 'h2' }: { lead: React.ReactNode; rest?: React.ReactNode; className?: string; as?: 'h2' | 'h3' }) {
  return (
    <Tag className={cx('vb-h2', className)}>
      {lead}
      {rest && (
        <>
          {' '}
          <span className="vb-two">{rest}</span>
        </>
      )}
    </Tag>
  );
}

export const Kicker = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <div className={cx('vb-label', className)}>{children}</div>
);
